import { baseApi } from './base.api';
import { projectsService } from './projectsService';
import { PATH_PROJECTS_API } from '../constants/api-routes';
import { ISO_DATE_FORMAT, URL_REGEX } from 'src/shared/constants';
import moment from 'moment';
import { decodeEntities } from 'src/utils/decodeEntities';
import { getApiVersion } from 'src/utils/apiVersion';

// A message in a conversation; dates are converted to local time when loaded.
export interface ProjectChatMessage {
  _id: string;
  owner?: string;
  body: string;
  created_at?: string;
  [key: string]: unknown;
}

// A conversation in the list, with the subject and status label added when loaded.
export interface ProjectChat {
  _id?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  subject?: string;
  friendlyStatus?: string;
  messages: ProjectChatMessage[];
  [key: string]: unknown;
}

export interface ProjectChatsPage {
  results: ProjectChat[];
  pagination?: { current_page?: number; total_pages?: number; total_items?: number };
  [key: string]: unknown;
}

// Filters, paging and order; dates and paging are '' when not set.
export interface ProjectChatsArgs {
  project_id?: string | number | null;
  service?: string;
  created_at_start?: string;
  created_at_end?: string;
  updated_at_start?: string;
  updated_at_end?: string;
  page?: number | '';
  page_size?: number | '';
  chat_status?: string[];
  escalation_reason_category?: string[];
  account?: string;
  order_by?: string | string[];
  types?: string[];
  sync?: boolean;
}

// Every match of a global regex, as strings.
const findAllMatches = (text: string, regex: RegExp) =>
  Array.from(text.matchAll(regex), ([match]) => match);

export const projectChatsService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      getProjectChats: build.query<ProjectChatsPage, ProjectChatsArgs>({
        query: ({
          project_id = '',
          service = '',
          created_at_start = '',
          created_at_end = '',
          updated_at_start = '',
          updated_at_end = '',
          page = '',
          page_size = '',
          chat_status = [],
          escalation_reason_category = [],
          account = '',
          order_by = '',
          types = [],
          sync,
        }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${project_id}/${PATH_PROJECTS_API.CHATS}?${chat_status
              .map((status) => {
                return `chat_status=${status}`;
              })
              .join('&')}&${escalation_reason_category
              .map((reason) => {
                return `escalation_reason_category=${reason}`;
              })
              .join('&')}&${types
              .map((type) => {
                return `types=${type}`;
              })
              .join('&')}`,
            params: {
              sync: sync,
              ...(project_id !== '' && { project_id }),
              ...(service !== '' && { service }),
              ...(created_at_start !== '' && {
                created_at_start: moment(created_at_start).utc().format(ISO_DATE_FORMAT),
              }),
              ...(created_at_end !== '' && {
                created_at_end: moment(created_at_end).utc().format(ISO_DATE_FORMAT),
              }),
              ...(updated_at_start !== '' && {
                updated_at_start: moment(updated_at_start).utc().format(ISO_DATE_FORMAT),
              }),
              ...(updated_at_end !== '' && {
                updated_at_end: moment(updated_at_end).utc().format(ISO_DATE_FORMAT),
              }),
              ...(page !== '' && { page }),
              ...(page_size !== '' && { page_size }),
              ...(account !== '' && { account }),
              ...(order_by !== '' && { order_by }),
            },
          };
        },
        transformResponse(response: ProjectChatsPage) {
          const results = response.results.map((chat) => {
            const userMessage = chat.messages.find(
              (message) => message.owner === 'user' && Boolean(message.body),
            );

            // Convert dates to the local time
            chat.updated_at = moment.utc(chat.updated_at).local().format(ISO_DATE_FORMAT);
            chat.created_at = moment.utc(chat.created_at).local().format(ISO_DATE_FORMAT);
            // Create subject based on first message
            const firstMessageBody = chat.messages[0] ? chat.messages[0].body : '...';
            chat.subject = decodeEntities(userMessage ? userMessage.body : firstMessageBody);

            if (chat.status === 'open') {
              chat.friendlyStatus = 'In Progress';
            }
            if (['closed', 'auto_closed'].includes(chat.status)) {
              chat.friendlyStatus = 'Resolved';
            }
            if (chat.status === 'escalated') {
              chat.friendlyStatus = 'Escalated';
            }

            chat.messages.forEach((message) => {
              const matches = findAllMatches(message.body, URL_REGEX);

              // Make links in Conversations History Page clickable
              if (matches.length) {
                for (const match of matches) {
                  const prettyUrl = match.replace(/'/g, '');
                  // convert link to Markdown format like: [link](https://cosupport.ai)
                  message.body = message.body.replace(match, `[${prettyUrl}](${prettyUrl})`);
                }
              }

              // Convert date to the local time
              message.created_at = moment.utc(message.created_at).local().format(ISO_DATE_FORMAT);
            });

            return chat;
          });

          return {
            ...response,
            results,
          };
        },
        async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
          try {
            const { data: chats } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${project_id}` },
                (draft) => {
                  draft.chats = chats;
                },
              ),
            );
          } catch (err) {
            console.error('Error get project files:', err);
          }
        },
        providesTags: ['ProjectChats'],
      }),
    };
  },
});

export const { useLazyGetProjectChatsQuery } = projectChatsService;
